// ImageField shows a single representative photo before it is uploaded.
//
// A bare <input type="file"> tells you "1 archivo" and nothing else — you cannot
// see whether you picked the right photo, or drop it if you picked the wrong one.
// This swaps the "Elegir foto" prompt for a thumbnail of the chosen file and gives
// a ✕ to clear it, so the admin sees exactly what the catalog will store.
//
// Island Architecture: a light-DOM web component, so the <input> it wraps still
// takes part in the surrounding <form> submission (multipart).

const MAX_BYTES = 8 * 1024 * 1024; // matches the server cap

export class ImageField extends HTMLElement {
  private input!: HTMLInputElement;
  private preview!: HTMLImageElement;
  private cta!: HTMLElement;
  private clearBtn!: HTMLButtonElement;
  private objectURL: string | null = null;

  connectedCallback(): void {
    const input = this.querySelector<HTMLInputElement>("[data-image-input]");
    const preview = this.querySelector<HTMLImageElement>("[data-image-preview]");
    const cta = this.querySelector<HTMLElement>("[data-image-cta]");
    const clearBtn = this.querySelector<HTMLButtonElement>("[data-image-clear]");
    if (!input || !preview || !cta || !clearBtn) return;

    this.input = input;
    this.preview = preview;
    this.cta = cta;
    this.clearBtn = clearBtn;

    this.input.addEventListener("change", (): void => this.onPick());
    this.clearBtn.addEventListener("click", (): void => this.clear());

    // Dropping a photo straight onto the box is the obvious gesture.
    const drop = this.querySelector<HTMLElement>("[data-image-drop]");
    if (drop) {
      for (const type of ["dragenter", "dragover"]) {
        drop.addEventListener(type, (e: Event): void => {
          e.preventDefault();
          drop.classList.add("is-dragging");
        });
      }
      for (const type of ["dragleave", "drop"]) {
        drop.addEventListener(type, (): void => drop.classList.remove("is-dragging"));
      }
      drop.addEventListener("drop", (e: DragEvent): void => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (!file) return;
        const dt = new DataTransfer();
        dt.items.add(file);
        this.input.files = dt.files;
        this.onPick();
      });
    }
  }

  private onPick(): void {
    const file = this.input.files?.[0];
    if (!file) {
      this.clear();
      return;
    }
    if (!file.type.startsWith("image/") || file.size > MAX_BYTES) {
      // Reject client-side too; the server enforces the same limits.
      this.clear();
      return;
    }
    this.revoke();
    this.objectURL = URL.createObjectURL(file);
    this.preview.src = this.objectURL;
    this.preview.hidden = false;
    this.cta.hidden = true;
    this.clearBtn.hidden = false;
  }

  private clear(): void {
    this.revoke();
    this.input.value = "";
    this.preview.hidden = true;
    this.preview.removeAttribute("src");
    this.cta.hidden = false;
    this.clearBtn.hidden = true;
  }

  private revoke(): void {
    if (this.objectURL) {
      URL.revokeObjectURL(this.objectURL);
      this.objectURL = null;
    }
  }

  disconnectedCallback(): void {
    this.revoke();
  }
}
